import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { getAdminApiError } from '../lib/adminApiErrors';
import {
  parseUserTableQuery,
  serializeUserTableQuery,
  type UserTableFilters,
  type UserTableQueryState,
} from '../lib/adminUsers';
import { useSuperAdminStore } from '../store/useSuperAdminStore';
import type { AdminUserStatus } from '../types';
import type {
  AdminDataTableFilterValues,
  AdminDataTableSortState,
} from './components';
import { useAdminToast } from './components';
import UserDrawer from './UserDrawer';
import UserTable from './UserTable';
import { AdminRouteErrorStack } from './AdminShell';

type OpenDrawer = { mode: 'invite' } | { mode: 'edit'; userId: number };

const QueryKeys = ['name', 'email', 'company', 'role', 'status', 'sort'];

const normalizeFilters = (filters: AdminDataTableFilterValues): UserTableFilters => ({
  name: filters.name ?? '',
  email: filters.email ?? '',
  company: filters.company ?? '',
  role: filters.role ?? '',
  status: filters.status ?? '',
});

const useUserRouteStore = () => ({
  users: useSuperAdminStore((state) => state.users),
  userDetail: useSuperAdminStore((state) => state.userDetail),
  userLoading: useSuperAdminStore((state) => state.userLoading),
  userError: useSuperAdminStore((state) => state.userError),
  companies: useSuperAdminStore((state) => state.companies),
  companyLoading: useSuperAdminStore((state) => state.companyLoading),
  companyError: useSuperAdminStore((state) => state.companyError),
  licenses: useSuperAdminStore((state) => state.licenses),
  licenseLoading: useSuperAdminStore((state) => state.licenseLoading),
  licenseError: useSuperAdminStore((state) => state.licenseError),
  fetchUsers: useSuperAdminStore((state) => state.fetchUsers),
  fetchCompanies: useSuperAdminStore((state) => state.fetchCompanies),
  fetchLicenses: useSuperAdminStore((state) => state.fetchLicenses),
  loadUserDetail: useSuperAdminStore((state) => state.loadUserDetail),
  clearUserDetail: useSuperAdminStore((state) => state.clearUserDetail),
  createUser: useSuperAdminStore((state) => state.createUser),
  saveUser: useSuperAdminStore((state) => state.saveUser),
  deleteUser: useSuperAdminStore((state) => state.deleteUser),
  resetUserPassword: useSuperAdminStore((state) => state.resetUserPassword),
  bulkUsers: useSuperAdminStore((state) => state.bulkUsers),
});

const UserRouteErrors: React.FC<{
  companyError: string | null;
  licenseError: ReturnType<typeof useUserRouteStore>['licenseError'];
  userError: ReturnType<typeof useUserRouteStore>['userError'];
  showCompanyError: boolean;
  showLicenseError: boolean;
  showUserError: boolean;
}> = (props) => (
  <>
    {props.showUserError && props.userError && (
      <div className="admin-users-error" role="alert">Unable to load users: {props.userError.message}</div>
    )}
    {props.showCompanyError && props.companyError && (
      <div className="admin-users-error" role="alert">Unable to load companies: {props.companyError}</div>
    )}
    {props.showLicenseError && props.licenseError && (
      <div className="admin-users-error" role="alert">Unable to load licenses: {props.licenseError.message}</div>
    )}
  </>
);

const useUserRouteDrawer = (
  store: ReturnType<typeof useUserRouteStore>,
  showToast: ReturnType<typeof useAdminToast>['showToast']
) => {
  const [drawer, setDrawer] = React.useState<OpenDrawer | null>(null);
  const detailRequest = React.useRef(0);
  const openEdit = async (userId: number) => {
    const request = ++detailRequest.current;
    setDrawer({ mode: 'edit', userId });
    try {
      await store.loadUserDetail(userId);
    } catch (error) {
      if (request !== detailRequest.current) {
        return;
      }
      const failure = getAdminApiError(error, 'user_detail_failed');
      showToast(`User details failed to load (${failure.code}).`, { tone: 'critical' });
      setDrawer(null);
      store.clearUserDetail();
    }
  };
  const close = () => {
    detailRequest.current += 1;
    setDrawer(null);
    store.clearUserDetail();
  };
  return {
    activeDetail: drawer?.mode === 'edit' && store.userDetail?.id === drawer.userId
      ? store.userDetail
      : null,
    close,
    drawer,
    invite: () => setDrawer({ mode: 'invite' }),
    openEdit,
  };
};

const AdminUsersRoute: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useAdminToast();
  const store = useUserRouteStore();
  const drawerState = useUserRouteDrawer(store, showToast);
  const [selectedIds, setSelectedIds] = React.useState<number[]>([]);
  const [now] = React.useState(() => new Date());
  const queryState = React.useMemo(() => parseUserTableQuery(searchParams), [searchParams]);
  const companyIdFilter = React.useMemo(() => {
    const value = Number(searchParams.get('companyId'));
    return Number.isInteger(value) && value > 0 ? value : null;
  }, [searchParams]);
  const visibleUsers = React.useMemo(() => companyIdFilter === null
    ? store.users
    : store.users.filter((user) => user.companyId === companyIdFilter), [companyIdFilter, store.users]);
  const { fetchCompanies, fetchLicenses, fetchUsers } = store;

  React.useEffect(() => {
    void Promise.all([fetchUsers(), fetchCompanies(), fetchLicenses()]);
  }, [fetchCompanies, fetchLicenses, fetchUsers]);

  React.useEffect(() => {
    const availableIds = new Set(visibleUsers.map((user) => user.id));
    setSelectedIds((current) => current.filter((id) => availableIds.has(id)));
  }, [visibleUsers]);

  const writeQuery = (state: UserTableQueryState) => {
    const next = new URLSearchParams(searchParams);
    QueryKeys.forEach((key) => next.delete(key));
    serializeUserTableQuery(state).forEach((value, key) => next.set(key, value));
    setSearchParams(next, { replace: true });
  };

  const changeStatus = (userId: number, status: AdminUserStatus) =>
    store.saveUser(userId, { status });
  const busy = store.userLoading || store.companyLoading || store.licenseLoading;
  const showCompanyError = store.companies.length === 0;
  const showLicenseError = store.licenses.length === 0;
  const showUserError = store.users.length === 0 && drawerState.drawer === null;
  const hasRouteError = Boolean(
    (showCompanyError && store.companyError)
    || (showLicenseError && store.licenseError)
    || (showUserError && store.userError)
  );

  return (
    <>
      <AdminRouteErrorStack active={hasRouteError}>
        <UserRouteErrors
          companyError={store.companyError}
          licenseError={store.licenseError}
          userError={store.userError}
          showCompanyError={showCompanyError}
          showLicenseError={showLicenseError}
          showUserError={showUserError}
        />
        <UserTable
          users={visibleUsers}
          licenses={store.licenses}
          onInvite={store.companies.length > 0 ? drawerState.invite : undefined}
          onOpen={(user) => void drawerState.openEdit(user.id)}
          onBulkAction={store.bulkUsers}
          onResetPassword={store.resetUserPassword}
          onChangeStatus={changeStatus}
          filterValues={queryState.filters}
          onFilterValuesChange={(filters) => writeQuery({ filters: normalizeFilters(filters), sort: queryState.sort })}
          sort={queryState.sort as AdminDataTableSortState}
          onSortChange={(sort) => writeQuery({ filters: queryState.filters, sort })}
          selectedIds={selectedIds}
          onSelectedIdsChange={(ids) => setSelectedIds(ids.map(Number).filter(Number.isFinite))}
          busy={busy}
          now={now}
        />
      </AdminRouteErrorStack>
      {drawerState.drawer && (
        <UserDrawer
          open
          mode={drawerState.drawer.mode}
          user={drawerState.activeDetail}
          users={store.users}
          companies={store.companies}
          licenses={store.licenses}
          loading={drawerState.drawer.mode === 'edit'
            && store.userLoading
            && drawerState.activeDetail === null}
          now={now}
          onClose={drawerState.close}
          onCreate={store.createUser}
          onSave={store.saveUser}
          onChangeStatus={changeStatus}
          onDelete={store.deleteUser}
          onResetPassword={store.resetUserPassword}
        />
      )}
    </>
  );
};

export default AdminUsersRoute;
